import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

type Step = 1 | 2 | 3;

interface TestResult {
  valid: boolean;
  hubId?: string;
  portalName?: string;
  error?: string;
}

const HS_DRAFT_KEY = "hs-create-draft";

function loadHsDraft() {
  try { const r = sessionStorage.getItem(HS_DRAFT_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
function saveHsDraft(data: { accessToken: string; displayName: string; testResult: TestResult }) {
  try { sessionStorage.setItem(HS_DRAFT_KEY, JSON.stringify(data)); } catch { /* ignore */ }
}
function clearHsDraft() {
  try { sessionStorage.removeItem(HS_DRAFT_KEY); } catch { /* ignore */ }
}

export default function HubSpotCreate() {
  const navigate = useNavigate();
  const api = useApi();

  const draft = loadHsDraft();
  const [step, setStep] = useState<Step>(draft?.testResult?.valid ? 2 : 1);
  const [displayName, setDisplayName] = useState(draft?.displayName ?? "");
  const [accessToken, setAccessToken] = useState(draft?.accessToken ?? "");
  const [pushTranscripts, setPushTranscripts] = useState(true);
  const [createIfMissing, setCreateIfMissing] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(draft?.testResult ?? null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleTestToken() {
    if (!accessToken.trim()) { setError("Enter your HubSpot access token."); return; }
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const resp = await api.admin.post("HubSpot/Configs/Test", { accessToken: accessToken.trim() });
      const data = await resp.json() as TestResult;
      setTestResult(data);
      if (data.valid) {
        const name = displayName || data.portalName || "HubSpot";
        if (!displayName) setDisplayName(name);
        saveHsDraft({ accessToken: accessToken.trim(), displayName: name, testResult: data });
        setStep(2);
      } else {
        setError(data.error || "Connection test failed. Check your token.");
      }
    } catch {
      setError("Failed to test connection. Please try again.");
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const resp = await api.admin.post("HubSpot/Configs", {
        displayName: displayName.trim() || testResult?.portalName || "HubSpot",
        accessToken: accessToken.trim(),
        pushTranscripts,
        createContactIfMissing: createIfMissing
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(body?.error ?? "Failed to save. Please try again.");
        return;
      }

      clearHsDraft();
      setStep(3);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  const steps = [
    { n: 1, label: "Connect" },
    { n: 2, label: "Settings" },
    { n: 3, label: "Done" }
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.n ? "bg-cornflower-blue-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                {step > s.n ? "✓" : s.n}
              </div>
              <span className={`text-sm ${step === s.n ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-3" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 1: Enter token ── */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Connect your HubSpot account</h2>
          <p className="text-sm text-slate-500 mb-6">
            Create a Private App in HubSpot and paste its access token here.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800 space-y-2">
            <p className="font-medium">How to get your HubSpot Private App token:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Go to <strong>HubSpot Settings → Integrations → Private Apps</strong></li>
              <li>Click <strong>Create a Private App</strong></li>
              <li>Give it a name (e.g. "Velaro Integration")</li>
              <li>Under <strong>Scopes</strong>, enable: <code className="bg-blue-100 px-1 rounded">crm.objects.contacts.read</code>, <code className="bg-blue-100 px-1 rounded">crm.objects.contacts.write</code>, <code className="bg-blue-100 px-1 rounded">crm.objects.notes.write</code></li>
              <li>Click <strong>Create App</strong> and copy the token</li>
            </ol>
            <a href="https://developers.hubspot.com/docs/api/private-apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm">View HubSpot docs →</a>
          </div>

          <label className="flex flex-col gap-1 mb-4">
            <span className="text-sm font-medium">Access Token</span>
            <input
              type="password"
              className="input font-mono text-sm"
              placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              autoFocus
            />
            <span className="text-xs text-slate-400">Starts with <code>pat-</code>. Never share this token.</span>
          </label>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <div className="flex justify-end gap-2">
            <SecondaryBlueButton label="Cancel" onClick={() => { clearHsDraft(); navigate("/integrations"); }} />
            {testing ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner /> Testing connection…</div>
            ) : (
              <PrimaryBlueButton label="Test & Continue" onClick={handleTestToken} />
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: Settings ── */}
      {step === 2 && testResult?.valid && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-lg">✓</span>
            </div>
            <div>
              <div className="font-semibold text-slate-800">Connected to HubSpot</div>
              <div className="text-sm text-slate-500">Portal: {testResult.portalName} (Hub ID: {testResult.hubId})</div>
            </div>
          </div>

          <label className="flex flex-col gap-1 mb-5">
            <span className="text-sm font-medium">Display Name</span>
            <input
              className="input"
              placeholder="e.g. Production HubSpot"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <span className="text-xs text-slate-400">How this connection appears in Velaro.</span>
          </label>

          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-semibold text-slate-700">Transcript Settings</h3>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={pushTranscripts}
                onChange={(e) => setPushTranscripts(e.target.checked)}
                className="mt-0.5 w-4 h-4"
              />
              <div>
                <div className="text-sm font-medium text-slate-800">Push transcripts to HubSpot</div>
                <div className="text-xs text-slate-500">When a conversation ends, automatically create a Note on the matched HubSpot contact with the full chat transcript.</div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={createIfMissing}
                onChange={(e) => setCreateIfMissing(e.target.checked)}
                className="mt-0.5 w-4 h-4"
              />
              <div>
                <div className="text-sm font-medium text-slate-800">Create contact if not found</div>
                <div className="text-xs text-slate-500">If the visitor's email doesn't match an existing HubSpot contact, create a new one automatically.</div>
              </div>
            </label>
          </div>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <div className="flex justify-between">
            <SecondaryBlueButton label="Back" onClick={() => setStep(1)} />
            {saving ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner /> Saving…</div>
            ) : (
              <PrimaryBlueButton label="Save & Finish" onClick={handleSave} />
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Done ── */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-500 text-3xl">✓</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">HubSpot connected!</h2>
          <p className="text-sm text-slate-500 mb-6">
            Your HubSpot CRM is now linked to Velaro.{" "}
            {pushTranscripts ? "Chat transcripts will be pushed automatically when conversations end." : ""}
          </p>
          <div className="flex justify-center gap-3">
            <SecondaryBlueButton label="Back to Integrations" onClick={() => navigate("/integrations")} />
            <PrimaryBlueButton label="Manage HubSpot" onClick={() => navigate("/integrations/hubspot")} />
          </div>
        </div>
      )}
    </div>
  );
}
